import { useMemo, useEffect } from "react";
import {
  Box,
  Text,
  Breadcrumb,
  BreadcrumbItem,
  Container,
  Heading,
  Stack,
  VStack,
  HStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
  useDisclosure,
  CircularProgress,
} from "@chakra-ui/react";

import { FiMoreVertical } from "react-icons/fi";
import { useHistory } from "react-router-dom";

import ArrowForwardIosRoundedIcon from "@material-ui/icons/ArrowForwardIosRounded";
import Header from "components/dashHeader";
import Sidemenu from "components/sidemenu";
import Button from "components/Button/Button";
import Confirmation from "components/Modals/p2p/Confirmation";
import BreadcrumbLink from "components/BreadcrumbLink/BreadcrumbLink";

import { ReactComponent as EmptyStateIcon } from "assets/svg/empty-inpl.svg";
import { ReactComponent as AddGreenIcon } from "assets/svg/add-green.svg";

import { useGetPriceAlertsQuery } from "services/rateApi";
import { isEmpty } from "lodash";
import toPrecision from "util/toPrecision";
import toCoinLocale from "util/toCoinLocale";
import { format, parseISO } from "date-fns";

import { useDeletePriceAlertMutation } from "services/rateApi";

function EmptyNotificationState() {
  const history = useHistory();

  return (
    <VStack maxW="xs" textAlign="center" m="0 auto" color="#4E4B66" py="2em">
      <Text fontSize="sm">
        Get notified when certain coin moves up or down in the market.
      </Text>

      <VStack mt="64px !important">
        <Box as={EmptyStateIcon} />
        <Text fontSize="sm">No notifications to display!</Text>
      </VStack>

      <Button
        mt="56px !important"
        minW="320px"
        minH="64px"
        onClick={() => history.push("/dashboard/account/pricealert/new")}
      >
        Add Price Alert
      </Button>
    </VStack>
  );
}

function Alert({ _id, direction, price, ticker, type, createdAt, refetch }) {
  const [coin] = String(ticker).split("usdt");

  const history = useHistory();
  const toast = useToast();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [deleteAlert, { isLoading, isSuccess, isError, error }] =
    useDeletePriceAlertMutation();

  const movement = useMemo(() => {
    const map = {
      up: "Above",
      down: "Below",
    };

    return map[direction];
  }, [direction]);

  const alertValue = useMemo(() => {
    const map = {
      price: toPrecision(price, "usd"),
      percentage: `${price}%`,
    };

    return map[type ?? "price"];
  }, [type, price]);

  const getError = (error) => {
    function isObj(obj) {
      var type = typeof obj;
      return type === "function" || (type === "object" && !!obj);
    }
    const value = isObj(error) ? Object.values(error)[0] : error;
    // console.log("ERROR VALUE", value);
    return value ?? "An unexpected error occurred";
  };

  useEffect(() => {
    if (isSuccess) {
      onClose();
      refetch && refetch();
    }

    if (isError && error) {
      toast({
        position: "bottom-right",
        title: "Error",
        description: getError(error?.data?.error),
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
  }, [isSuccess, isError, error, onClose, refetch, toast]);

  return (
    <Box
      shadow="0px 0px 5px rgba(0, 0, 0, 0.05)"
      borderRadius="10px"
      border="1px solid #C4C4C4"
      p="14px 18px"
      pos="relative"
    >
      <HStack fontWeight="600">
        <Text>Coin Type:</Text>
        <Text>{toCoinLocale(coin)}</Text>
      </HStack>
      <HStack color="#4E4B66" fontWeight="500" mt="0px" fontSize="sm">
        <Text fontWeight="500">{movement},</Text>
        <Text ml="2px !important">{alertValue}</Text>
      </HStack>
      <HStack color="#4E4B66" mt="0px" fontSize="sm">
        {/* <Text fontWeight="500">Above,</Text> */}
        <Text>{format(parseISO(createdAt), "dd-MM-yyyy")}</Text>
      </HStack>

      <Box pos="absolute" top="20px" right="20px">
        <Menu>
          <MenuButton>
            <Box as={FiMoreVertical} fontSize="20px" />
          </MenuButton>
          <MenuList>
            <MenuItem
              onClick={() =>
                history.push(`/dashboard/account/pricealert/${_id}/edit`)
              }
            >
              Edit
            </MenuItem>
            <MenuItem onClick={onOpen}>Delete</MenuItem>
          </MenuList>
        </Menu>
      </Box>

      <Confirmation
        isOpen={isOpen}
        onClose={onClose}
        isLoading={isLoading}
        onConfirm={() => deleteAlert(_id)}
        title="Delete Price Alert"
      >
        <VStack>
          <Text>Are you sure you want to delete this price alert?</Text>
        </VStack>
      </Confirmation>
    </Box>
  );
}

export default function CryptoAlert(props) {
  //   const { userData } = props;
  const history = useHistory();

  const { data, isLoading, isFetching, refetch } = useGetPriceAlertsQuery();

  const alerts = useMemo(() => data?.alerts, [data]);
  // console.log("ALERTS", alerts);

  return (
    <Box>
      <Sidemenu currentPage="Account" logoutUser={props.logout} />
      <Header />
      <Box as="section">
        <Box
          className="account-container"
          p={["6em 0 !important", "6.5em 1.5em !important"]}
        >
          <Container
            maxW={["100%", "100%", "calc(100% - 15vw)", "calc(100% - 35vw)"]}
            m="0"
            p={[0, 0, "1rem"]}
          >
            <Stack mx={["24px", 0]} mb={["24px", "32px"]}>
              <HStack>
                <Heading>Crypto Price Alert</Heading>
                {isFetching && (
                  <CircularProgress
                    size="26px"
                    color="green.400"
                    isIndeterminate
                  />
                )}
              </HStack>

              <Breadcrumb
                className="animate__animated animate__fadeInDown"
                separator={
                  <ArrowForwardIosRoundedIcon
                    style={{ fontSize: ".8em", color: "#6E7191" }}
                  />
                }
              >
                <BreadcrumbItem>
                  <BreadcrumbLink to="/dashboard/account">
                    Account
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbItem isCurrentPage>
                  <BreadcrumbLink isCurrentPage to="">
                    Crypto Price Alert
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </Breadcrumb>
            </Stack>

            {!isEmpty(alerts) && !isLoading && (
              <HStack w="100%" justifyContent="flex-end">
                <Button
                  size="sm"
                  bg="transparent"
                  color="var(--primary)"
                  leftIcon={<Box as={AddGreenIcon} />}
                  onClick={() =>
                    history.push("/dashboard/account/pricealert/new")
                  }
                >
                  Add Alert
                </Button>
              </HStack>
            )}

            <Box
              bg="white"
              p={["32px 24px", "32px 24px", "46px"]}
              mt={["24px", "10px"]}
              borderRadius={[0, "8px", "16px"]}
            >
              {isEmpty(alerts) && !isLoading && <EmptyNotificationState />}

              {isEmpty(alerts) && isLoading && (
                <VStack>
                  <CircularProgress
                    size="18px"
                    color="green.400"
                    isIndeterminate
                  />
                </VStack>
              )}

              {!isEmpty(alerts) && !isLoading && (
                <Box>
                  <Text mb="20px" fontSize="md">
                    Get notified when certain coin moves up or down in the
                    market.
                  </Text>
                  <Stack>
                    {alerts?.map((alert) => (
                      <Alert refetch={refetch} {...alert} />
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>
          </Container>
        </Box>
      </Box>
    </Box>
  );
}
